import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  Calendar,
  Package,
  CreditCard,
  Shield,
  ArrowRight,
} from "lucide-react";
import axios from "axios";
export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    container_type: "",
    quantity: 1,
    start_dt: "",
    booking_duration: "",
    storage_use: "",
    user_nm: "",
    user_email: "",
    user_phone: "",
    address: "",
    amount: "",
    city: "",
    postcode: "",
    business_name: "",
    business_type: "",
    storage_desc: "",
    access_frequency: "",
    terms: "unchecked",
  });

  const [errors, setErrors] = useState({});

  const { data: containerOptions } = useQuery({
    queryKey: ["containerOptions"],
    queryFn: async () => {
      const data = await axios
        .get("https://api.saharstorage.com/api/cont_opts/")
        .then((res) => res.data);
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const durationOptions = [
    { value: "1", label: "1 Month", discount: 0 },
    { value: "3", label: "3 Months", discount: 5 },
    { value: "6", label: "6 Months", discount: 10 },
    { value: "12", label: "12 Months", discount: 15 },
    { value: "12+", label: "12+ Months", discount: 20 },
  ];

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.container_type)
        newErrors.container_type = "Please select a container type";
      if (!formData.start_dt) newErrors.start_dt = "Please select a start date";
      if (!formData.duration)
        newErrors.duration = "Please select storage duration";
      if (!formData.storage_use)
        newErrors.storage_use = "Please select storage use type";
    }

    if (currentStep === 2) {
      if (!formData.user_nm) newErrors.user_nm = "Full name is required";
      if (!formData.user_email)
        newErrors.user_email = "Email address is required";
      if (!formData.user_phone)
        newErrors.user_phone = "Phone number is required";
      if (!formData.address) newErrors.address = "Address is required";
      if (!formData.city) newErrors.city = "City is required";
      if (!formData.postcode) newErrors.postcode = "Postcode is required";

      if (formData.storage_use === "business") {
        if (!formData.business_name)
          newErrors.business_name = "Business name is required";
        if (!formData.business_type)
          newErrors.business_type = "Business type is required";
      }
    }

    if (currentStep === 3) {
      if (!formData.storage_desc)
        newErrors.storage_desc = "Please describe items to store";
      if (!formData.access_frequency)
        newErrors.access_frequency = "Please select access frequency";
      if (!formData.terms)
        newErrors.terms = "You must accept the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const calculateTotal = () => {
    const flattenedContainers = containerOptions?.flat() || [];
    const container = flattenedContainers.find(
      (c) => c.container_id === formData.container_type,
    );
    if (!container) return { monthly: 0, total: 0, discount: 0 };

    const basePrice = parseInt(container.price.replace("£", ""));
    const subtotal = basePrice * formData.quantity;
    const total = subtotal;

    return {
      monthly: basePrice * formData.quantity,
      subtotal,
      total,
    };
  };
  const costs = calculateTotal();

  const handleSubmit = async () => {
    const bookingRes = await axios.post(
      "https://api.saharstorage.com/api/booking/",
      formData,
    );
    if (bookingRes.status === 201) {
      const stripeRes = await axios.post(
        "https://api.saharstorage.com/api/create-checkout/",
        {
          user_id: formData.user_id,
          amount: costs.total,
          user_email: formData.user_email,
          container_type: formData.container_type,
          quantity: formData.quantity,
          name: formData.user_nm,
          phone: formData.user_phone,
          address: formData.address,
          city: formData.city,
          postcode: formData.postcode,
          start_date: formData.start_dt,
          booking_duration: formData.duration,
          storage_use: formData.storage_use,
        },
      );
      if (stripeRes.status === 200) {
        window.location.href = stripeRes.data.checkout_url;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-white">
      {/* Progress Steps Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-3">
              {[
                { num: 1, label: "Container" },
                { num: 2, label: "Contact" },
                { num: 3, label: "Details" },
              ].map((s, idx) => (
                <div key={s.num} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                        step >= s.num
                          ? "bg-zinc-900 text-white shadow-lg scale-110"
                          : "bg-zinc-200 text-zinc-500"
                      }`}
                    >
                      {step > s.num ? <Check size={18} /> : s.num}
                    </div>
                    <span
                      className={`text-xs mt-2 font-medium hidden sm:block ${
                        step >= s.num ? "text-zinc-900" : "text-zinc-500"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div
                      className={`w-16 md:w-24 h-1 mx-2 rounded-full transition-all ${
                        step > s.num ? "bg-zinc-900" : "bg-zinc-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form Area */}
          <div className="lg:col-span-2">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl border border-zinc-200 p-8 md:p-10 shadow-lg"
            >
              {/* Step 1: Container Selection */}
              {step === 1 && (
                <div>
                  <div className="mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold mb-3">
                      Select Your Container
                    </h2>
                    <p className="text-zinc-600 text-lg">
                      Choose the container size and rental period that suits
                      your needs.
                    </p>
                  </div>

                  {/* Container Selection */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-zinc-700 mb-4">
                      Container Type *
                    </label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {containerOptions?.map((containerArray, arrayIndex) =>
                        containerArray.map((container, containerIndex) => (
                          <button
                            key={`${arrayIndex}-${container.conatainer_id || containerIndex}`}
                            type="button"
                            onClick={() =>
                              updateFormData(
                                "container_type",
                                container.container_id,
                              )
                            }
                            className={`p-6 rounded-xl border-2 text-left transition-all hover:shadow-lg ${
                              formData.container_type ===
                              container.conatainer_id
                                ? "border-zinc-900 bg-gradient-to-br from-zinc-50 to-white shadow-lg scale-[1.02]"
                                : "border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50"
                            }`}
                          >
                            <img
                              src={
                                "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400"
                              }
                              alt={container.name}
                              className="w-full h-32 object-cover rounded-lg mb-4"
                            />
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold text-lg">
                                {container.name}
                              </h3>
                              {formData.container_type ===
                                container.container_id && (
                                <Check
                                  size={20}
                                  className="text-zinc-800 flex-shrink-0"
                                />
                              )}
                            </div>
                            <div className="flex items-baseline gap-2 mb-2">
                              <span className="text-3xl font-bold text-zinc-900">
                                {container.price}
                              </span>
                              <span className="text-sm font-medium text-zinc-600">
                                /{container.period}
                              </span>
                            </div>
                            <div className="text-sm text-zinc-600 space-y-1">
                              <p>Dimensions: {container.specs}</p>
                              <p>Capacity: {container.capacity}</p>
                              <p className="text-emerald-600 font-medium">
                                {container.available} Available
                              </p>
                            </div>
                          </button>
                        )),
                      )}
                    </div>
                    {errors.container_type && (
                      <p className="text-red-600 text-sm mt-2">
                        {errors.container_type}
                      </p>
                    )}
                  </div>

                  {/* Quantity */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-zinc-700 mb-3">
                      Quantity
                    </label>
                    <select
                      value={formData.quantity}
                      onChange={(e) =>
                        updateFormData("quantity", parseInt(e.target.value))
                      }
                      className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                    >
                      {[1, 2, 3, 4, 5].map((num) => (
                        <option key={num} value={num}>
                          {num} Container{num > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-zinc-700 mb-3">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.start_dt}
                      onChange={(e) =>
                        updateFormData("start_dt", e.target.value)
                      }
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                    />
                    {errors.start_dt && (
                      <p className="text-red-600 text-sm mt-2">
                        {errors.start_dt}
                      </p>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-zinc-700 mb-3">
                      Storage Duration *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {durationOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            updateFormData("duration", option.value)
                          }
                          className={`p-4 rounded-xl border-2 text-center transition ${
                            formData.duration === option.value
                              ? "border-zinc-800 bg-zinc-50"
                              : "border-zinc-200 hover:border-zinc-300"
                          }`}
                        >
                          <div className="font-semibold text-sm mb-1">
                            {option.label}
                          </div>
                        </button>
                      ))}
                    </div>
                    {errors.duration && (
                      <p className="text-red-600 text-sm mt-2">
                        {errors.duration}
                      </p>
                    )}
                  </div>

                  {/* Storage Use */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-zinc-700 mb-3">
                      Storage Use *
                    </label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        {
                          value: "personal",
                          label: "Personal Use",
                          desc: "Household items, furniture, personal belongings",
                        },
                        {
                          value: "business",
                          label: "Business Use",
                          desc: "Inventory, equipment, commercial storage",
                        },
                      ].map((use) => (
                        <button
                          key={use.value}
                          type="button"
                          onClick={() =>
                            updateFormData("storage_use", use.value)
                          }
                          className={`p-4 rounded-xl border-2 text-left transition ${
                            formData.storage_use === use.value
                              ? "border-zinc-800 bg-zinc-50"
                              : "border-zinc-200 hover:border-zinc-300"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{use.label}</span>
                            {formData.storage_use === use.value && (
                              <Check size={18} />
                            )}
                          </div>
                          <p className="text-sm text-zinc-600">{use.desc}</p>
                        </button>
                      ))}
                    </div>
                    {errors.storage_use && (
                      <p className="text-red-600 text-sm mt-2">
                        {errors.storage_use}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Contact Information */}
              {step === 2 && (
                <div>
                  <div className="mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold mb-3">
                      Contact Information
                    </h2>
                    <p className="text-zinc-600 text-lg">
                      Please provide your contact details for the booking.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-3">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={formData.user_nm}
                        onChange={(e) =>
                          updateFormData("user_nm", e.target.value)
                        }
                        className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                        placeholder="John Smith"
                      />
                      {errors.user_nm && (
                        <p className="text-red-600 text-sm mt-2">
                          {errors.user_nm}
                        </p>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-3">
                          Email Address *
                        </label>
                        <input
                          type="user_email"
                          value={formData.user_email}
                          onChange={(e) =>
                            updateFormData("user_email", e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                          placeholder="john@example.com"
                        />
                        {errors.user_email && (
                          <p className="text-red-600 text-sm mt-2">
                            {errors.user_email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-3">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={formData.user_phone}
                          onChange={(e) =>
                            updateFormData("user_phone", e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                          placeholder="07XXX XXXXXX"
                        />
                        {errors.user_phone && (
                          <p className="text-red-600 text-sm mt-2">
                            {errors.user_phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-3">
                        Address *
                      </label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) =>
                          updateFormData("address", e.target.value)
                        }
                        className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                        placeholder="123 High Street"
                      />
                      {errors.address && (
                        <p className="text-red-600 text-sm mt-2">
                          {errors.address}
                        </p>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-3">
                          City *
                        </label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) =>
                            updateFormData("city", e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                          placeholder="London"
                        />
                        {errors.city && (
                          <p className="text-red-600 text-sm mt-2">
                            {errors.city}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-3">
                          Postcode *
                        </label>
                        <input
                          type="text"
                          value={formData.postcode}
                          onChange={(e) =>
                            updateFormData("postcode", e.target.value)
                          }
                          className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                          placeholder="SW1A 1AA"
                        />
                        {errors.postcode && (
                          <p className="text-red-600 text-sm mt-2">
                            {errors.postcode}
                          </p>
                        )}
                      </div>
                    </div>

                    {formData.storage_use === "business" && (
                      <>
                        <div className="pt-6 border-t border-zinc-200">
                          <h3 className="text-lg font-semibold mb-4">
                            Business Information
                          </h3>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-3">
                            Business Name *
                          </label>
                          <input
                            type="text"
                            value={formData.business_name}
                            onChange={(e) =>
                              updateFormData("business_name", e.target.value)
                            }
                            className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                            placeholder="Your Company Ltd"
                          />
                          {errors.business_name && (
                            <p className="text-red-600 text-sm mt-2">
                              {errors.business_name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-3">
                            Business Type *
                          </label>
                          <select
                            value={formData.business_type}
                            onChange={(e) =>
                              updateFormData("business_type", e.target.value)
                            }
                            className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition"
                          >
                            <option value="">Select business type</option>
                            <option value="retail">Retail</option>
                            <option value="construction">Construction</option>
                            <option value="logistics">
                              Logistics/Transport
                            </option>
                            <option value="manufacturing">Manufacturing</option>
                            <option value="services">
                              Professional Services
                            </option>
                            <option value="other">Other</option>
                          </select>
                          {errors.business_type && (
                            <p className="text-red-600 text-sm mt-2">
                              {errors.business_type}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Storage Details */}
              {step === 3 && (
                <div>
                  <div className="mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold mb-3">
                      Storage Details
                    </h2>
                    <p className="text-zinc-600 text-lg">
                      Help us understand your storage needs better.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-3">
                        What will you be storing? *
                      </label>
                      <textarea
                        value={formData.storage_desc}
                        onChange={(e) =>
                          updateFormData("storage_desc", e.target.value)
                        }
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:border-zinc-800 focus:ring-2 focus:ring-zinc-200 outline-none transition resize-none"
                        placeholder="Please describe the items you plan to store (e.g., furniture, business inventory, tools, etc.)"
                      />
                      {errors.storage_desc && (
                        <p className="text-red-600 text-sm mt-2">
                          {errors.storage_desc}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-3">
                        How often will you need access? *
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { value: "daily", label: "Daily" },
                          { value: "weekly", label: "Weekly" },
                          { value: "monthly", label: "Monthly" },
                          { value: "rarely", label: "Rarely" },
                        ].map((freq) => (
                          <button
                            key={freq.value}
                            type="button"
                            onClick={() =>
                              updateFormData("access_frequency", freq.value)
                            }
                            className={`p-4 rounded-xl border-2 text-center transition ${
                              formData.access_frequency === freq.value
                                ? "border-zinc-800 bg-zinc-50"
                                : "border-zinc-200 hover:border-zinc-300"
                            }`}
                          >
                            <div className="font-medium">{freq.label}</div>
                          </button>
                        ))}
                      </div>
                      {errors.access_frequency && (
                        <p className="text-red-600 text-sm mt-2">
                          {errors.access_frequency}
                        </p>
                      )}
                    </div>

                    <div className="pt-6 border-t border-zinc-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.terms}
                          onChange={(e) =>
                            updateFormData("terms", e.target.checked)
                          }
                          className="w-5 h-5 rounded border-zinc-300 text-zinc-900 focus:ring-2 focus:ring-zinc-200 mt-0.5"
                        />
                        <span className="text-sm text-zinc-700">
                          I agree to the{" "}
                          <a
                            href="#"
                            className="text-zinc-900 underline font-medium"
                          >
                            Terms and Conditions
                          </a>{" "}
                          and{" "}
                          <a
                            href="#"
                            className="text-zinc-900 underline font-medium"
                          >
                            Privacy Policy
                          </a>
                          . I understand that payment is required before
                          container access is granted. *
                        </span>
                      </label>
                      {errors.terms && (
                        <p className="text-red-600 text-sm mt-2">
                          {errors.terms}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-8 border-t border-zinc-200 mt-8">
                {step > 1 ? (
                  <button
                    onClick={prevStep}
                    className="px-6 py-3 rounded-xl font-medium text-zinc-700 hover:bg-zinc-100 transition"
                  >
                    Back
                  </button>
                ) : (
                  <Link
                    to="/"
                    className="px-6 py-3 rounded-xl font-medium text-zinc-700 hover:bg-zinc-100 transition"
                  >
                    Cancel
                  </Link>
                )}

                {step < 3 ? (
                  <button
                    onClick={nextStep}
                    className="px-8 py-3 rounded-xl font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    Continue <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="px-8 py-3 rounded-xl font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    Submit Booking <Shield size={18} />
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Pricing Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-6 text-white shadow-xl">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <CreditCard size={24} />
                  Booking Summary
                </h3>

                {formData.container_type ? (
                  <div className="space-y-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Package size={20} />
                        <span className="font-semibold">
                          {
                            containerOptions
                              ?.flat()
                              .find((c) => c.id === formData.container_type)
                              ?.name
                          }
                        </span>
                      </div>
                      <div className="text-sm text-zinc-300">
                        Quantity: {formData.quantity}
                      </div>
                    </div>

                    {formData.duration && (
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                        <div className="flex items-center gap-3 mb-2">
                          <Calendar size={20} />
                          <span className="font-semibold">
                            {
                              durationOptions.find(
                                (d) => d.value === formData.duration,
                              )?.label
                            }
                          </span>
                        </div>
                        {costs.discountPercent > 0 && (
                          <div className="text-sm text-emerald-300 font-medium">
                            {costs.discountPercent}% discount applied
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pt-4 border-t border-white/20 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-300">Monthly Rate</span>
                        <span className="font-semibold">
                          £{costs.monthly.toFixed(2)}
                        </span>
                      </div>
                      {costs.discount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-300">Discount</span>
                          <span className="text-emerald-300 font-semibold">
                            -£{costs.discount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/20">
                        <span>Total</span>
                        <span>£{costs.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-zinc-400 text-sm text-center py-8">
                    Select a container to see pricing
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="flex items-start gap-3 text-sm text-zinc-300">
                    <Shield size={18} className="mt-0.5 flex-shrink-0" />
                    <span>
                      Secure booking process. Your information is protected.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
